import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { compose } from '../../../services/utilityFunctions';
import withCSRF from '../../../commonComponents/withCSRF';
import translate from '../../../commonComponents/translate';
import DisplayShippingContainer from './DisplayShippingContainer/DisplayShippingContainer';
import FormattedAddress from '../../shop/FormattedAddress';
import UserProp from '../../../commonComponents/propTypes/userProp';
import PenEditButton from '../../../commonComponents/buttons/PenEditButton';

function UserMainInfo({ t, user, onAddressOpen }) {
  const {
    email,
    phone,
    image,
    firstName,
    lastName,
    address,
    username,
    shippingCountry,
    personalInfo,
  } = user;
  const userImage = image?.thumb ? image.thumb : '/images/no_user.png';
  const name = firstName && lastName ? `${firstName} ${lastName}` : username;

  const isIndividual =
    !personalInfo?.businessType || personalInfo?.businessType === 'individual';

  const checkIfBillingIsValid = useCallback(() => {
    if (!address) {
      return false;
    }

    let isValid = true;
    // Check if its individual or company if individual check the country code
    if (isIndividual) {
      // Check if address is valid according to shipping country
      if (shippingCountry) {
        isValid = shippingCountry === address?.countryCode;
      }
    }
    return isValid;
  }, [user]);

  const formattedAddress = useMemo(
    () => ({
      ...address,
      firstName: address?.firstName || firstName,
      lastName: address?.lastName || lastName,
      email: address?.email || email,
      phone: address?.phone || phone,
      companyName: !isIndividual ? personalInfo.companyName : undefined,
      NIF: !isIndividual ? personalInfo.NIF : undefined,
    }),
    [user],
  );

  return (
    <div className="billing-wrapper" style={{ paddingLeft: '0' }}>
      <div className="row customer-img-name">
        <div>
          <img src={userImage} alt="/images/no_user.png" />
        </div>
        <p>{name}</p>
      </div>
      <div className="order-address-title-container">
        <p>{t('billing_address')}</p>
      </div>
      <div className={address ? '' : 'customer-info-invalid'}>
        {address ? (
          <div className="row order-address-container">
            <PenEditButton onClick={() => onAddressOpen('billing', address)} />
            <div className="col-12 address">
              <FormattedAddress address={formattedAddress} showOptionalFields />
            </div>
            {!checkIfBillingIsValid() && (
              <span className="col-12 warning-message">
                {t('order.create.errorMessage.shippingCountry')}
                <br />
                {t('order.create.errorMessage.shippingAddress')}
              </span>
            )}
          </div>
        ) : (
          <div className="customer-billing-error">
            {t('error_billing_address')}
            <button
              className="edit-address"
              type="button"
              onClick={() => onAddressOpen('billing', user)}
            >
              <i className="fa fa-user-edit" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

UserMainInfo.propTypes = {
  t: PropTypes.func.isRequired,
  user: PropTypes.shape(UserProp.propType).isRequired,
  onAddressOpen: PropTypes.func.isRequired,
};

function CustomerInfo({
  t,
  user,
  onShippingChange,
  shippingAddressIsDifferent,
  handleDifferentShippingAddress,
  isOnlyDigitalProducts,
  onAddressOpen,
}) {
  const { shippingAddress, shippingCountry } = user;

  /** Render the component */
  return (
    <div className="customer-info-bill-email-phone">
      <UserMainInfo user={user} t={t} onAddressOpen={onAddressOpen} />
      {isOnlyDigitalProducts === false && (
        <div className="shipping-wrapper">
          <div className="order-address-title-container">
            <p>{t('shop.cart.shipping')}</p>
            <div
              className="col-form-label shipping-ask"
              style={{ paddingTop: 0, paddingBottom: 0 }}
            >
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for */}
              <label className="m-checkbox m-checkbox--state-brand">
                <input
                  type="checkbox"
                  checked={shippingAddressIsDifferent}
                  onChange={handleDifferentShippingAddress}
                />
                {t('order.create.infoMsg.shippingDifferent')}
                <span />
              </label>
            </div>
          </div>
          {shippingAddressIsDifferent && (
            <div className="customer-info-shipping-address">
              <DisplayShippingContainer
                shipping={shippingAddress}
                onShippingChange={onShippingChange}
                isShippingAddress
                shippingCountry={shippingCountry}
                user={user}
                onAddressOpen={onAddressOpen}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

CustomerInfo.propTypes = {
  t: PropTypes.func.isRequired,
  user: PropTypes.shape(UserProp.propType).isRequired,
  onShippingChange: PropTypes.func.isRequired,
  handleDifferentShippingAddress: PropTypes.func.isRequired,
  shippingAddressIsDifferent: PropTypes.bool.isRequired,
  isOnlyDigitalProducts: PropTypes.bool.isRequired,
  onAddressOpen: PropTypes.func.isRequired,
};

export default compose(withCSRF, translate)(CustomerInfo);
